import axios, { AxiosProgressEvent, AxiosResponse } from 'axios'
import { FileAPIEntry, FileAction, FileState, GroupedURLResolver, ReUploadUrlResolver, SignedURLRetrieverFnc, UploadUrlResolver } from './fileAPI.types'
import { runInUploadPool } from './uploadPool'
import { releaseLease, trackLease } from './uploadLeases'

import { captureException } from '@sentry/react'
import { wrap } from 'comlink'
import { Nullable } from 'models/helpers'
import { SignedURLDTO } from 'models/visuals'
import { UseAPIReturn } from 'utils/API'
import { isRetryableRequestFailure, retryRequestUntilSuccess } from 'utils/API/retryRequestUntilSuccess'
import { logger } from 'utils/logging'
import { loadVisualWorkerType } from 'workers/loadFileWorker'

const fileLogger = logger.withContext({ component: 'FileAPI' })

/**
 * Per-wave minting turns one mint call per drop into ~N/waveSize of them, so a single transient
 * 429/502/token blip now has that many chances to land — and each one abandons the whole remainder.
 * A few quick attempts absorb the blip; past that the backend is genuinely down, not flickering.
 */
export const MINT_RETRY_ATTEMPTS = 3
export const MINT_RETRY_DELAY_MS = 500

/** Extracts structured error info from an Axios error for logging */
function extractUploadErrorDetails(e: unknown) {
  if (axios.isAxiosError(e)) {
    return {
      status: e.response?.status,
      statusText: e.response?.statusText,
      responseData: e.response?.data,
      message: e.message,
    }
  }

  return {
    message: e instanceof Error ? e.message : String(e),
  }
}

/** Builds a human-readable error string from an Axios error */
function buildErrorMessage(e: unknown, context: string): string {
  if (axios.isAxiosError(e)) {
    const status = e.response?.status
    const responseMessage = typeof e.response?.data === 'object'
      ? e.response?.data?.message || e.response?.data?.error || JSON.stringify(e.response?.data)
      : e.response?.data

    return `${context}: HTTP ${status} - ${responseMessage || e.message}`
  }

  return `${context}: ${e instanceof Error ? e.message : String(e)}`
}

export enum UploadType {
  STANDARD = 'STANDARD',
  RESUMABLE = 'RESUMABLE',
}

/** Error message reported when a 0-byte file is rejected before upload */
export const EMPTY_FILE_ERROR = 'File is empty (0 bytes). Re-add it from your local disk and try again.'

/**
 * Reports a 0-byte file rejected before upload and notifies the caller via onError.
 * The browser can hand over an unreadable `File` (cloud-offloaded placeholder, disconnected
 * volume, stale reference) that reads as empty — uploading it would finalize a 0-byte object
 * in GCS and fail image processing with EMPTY_IMAGE (ISSUE-2042).
 *
 * @returns true when the file is empty and the upload must be aborted
 */
export function rejectEmptyFile(file: File, action: string, onError: (error?: string) => void): boolean {
  if (file.size > 0) return false

  fileLogger.error('Rejected upload of empty (0-byte) file', null, {
    context: {
      action,
      metadata: { fileName: file.name, fileType: file.type },
    },
  })
  captureException(new Error('FileAPI rejected empty (0-byte) file before upload'), {
    extra: { fileName: file.name, fileType: file.type, action },
  })
  onError(EMPTY_FILE_ERROR)

  return true
}

export const FileAvailableActions = new Set([
  FileAction.UPLOAD,
  FileAction.RE_UPLOAD,
  FileAction.LOAD,
])

export const FileUnavailableActions = new Set([
  FileAction.DELETE,
  FileAction.INIT,
])

export const FinishedFileStates = new Set([
  FileState.CANCELLED,
  FileState.ERROR,
  FileState.SUCCESS,
])

export function removeLastFileExtension(fileName: string) {
  const tokenized = fileName.split('.')

  if (tokenized.length === 1) return fileName

  return tokenized.slice(0, -1).join('.')
}

export function getSignedURLRetrieverSeq<MetadataType extends { [key: string]: any }>(resolverFnc: UploadUrlResolver<MetadataType> | ReUploadUrlResolver<MetadataType>): SignedURLRetrieverFnc<MetadataType> {

  return async (files: Array<FileAPIEntry<MetadataType>>, api: UseAPIReturn<string>) => {
    const successfulCalls: Record<string, SignedURLDTO> = {}
    const cancelledCalls: string[] = []
    const errorCalls: string[] = []

    // Sequentially obtain signed urls for all files
    // Sequentially because if called too quickly GC can return duplicated filename => duplicated urls
    for (const file of files) {

      await resolverFnc(file, api)
        .then((response) => {
          successfulCalls[file.id] = response.data
        })
        .catch((e) => {
          if (axios.isCancel(e)) {
            cancelledCalls.push(file.id)
          } else {
            fileLogger.error('Signed URL retrieval failed (sequential)', e, {
              context: {
                action: 'signedUrlRetrieval',
                metadata: {
                  ...extractUploadErrorDetails(e),
                  fileName: file.fileObject.name,
                  fileType: file.fileObject.type,
                  fileSize: file.fileObject.size,
                },
              },
            })
            errorCalls.push(file.id)
          }
        })
    }

    return {
      successfulCalls,
      cancelledCalls,
      errorCalls,
    }
  }
}

export function getSignedURLRetrieverBatch<MetadataType extends { [key: string]: any }>(resolverFnc: GroupedURLResolver<MetadataType>): SignedURLRetrieverFnc<MetadataType> {

  return async (files: Array<FileAPIEntry<MetadataType>>, api: UseAPIReturn<string>) => {

    // Preserve relationship between internal id and filename to pair response to entry
    const filenameToIdMap = files.reduce<Record<string, string>>((map, entry) => {
      const name = entry.originalFilename ?? entry.fileObject.name

      if (!name) return map

      const newMap = { ...map }

      newMap[name] = entry.id

      return newMap
    }, {})

    let successfulCalls: Record<string, SignedURLDTO> = {}
    let cancelledCalls: string[] = []
    let errorCalls: string[] = []

    // Any rejection here marks the WHOLE wave as errored, which the caller reads as a refusal and
    // uses to abandon every file behind it — so a blip must not reach that path unretried.
    await retryRequestUntilSuccess<Record<string, SignedURLDTO>>(() => resolverFnc(files, api), MINT_RETRY_DELAY_MS, MINT_RETRY_ATTEMPTS, isRetryableRequestFailure)
      .then((response) => {
        // The helper RETURNS the last failure instead of throwing, and a network-level one is not
        // an AxiosError — checking for that alone would let it through as an empty response.
        if (response instanceof Error) throw response

        // Pair to entry ids and add to map
        for (const [filename, data] of Object.entries(response.data)) {
          successfulCalls[filenameToIdMap[filename]] = data
        }
      })
      .catch((e) => {
        const ids = files.map((file) => file.id)

        if (axios.isCancel(e)) {
          cancelledCalls = ids
        } else {
          fileLogger.error('Signed URL batch retrieval failed', e, {
            context: {
              action: 'signedUrlRetrieval',
              metadata: {
                ...extractUploadErrorDetails(e),
                fileCount: files.length,
                files: files.slice(0, 10).map(f => ({
                  name: f.fileObject.name,
                  type: f.fileObject.type,
                  size: f.fileObject.size,
                })),
              },
            },
          })
          errorCalls = ids
        }
      })

    return {
      successfulCalls,
      cancelledCalls,
      errorCalls,
    }
  }
}
/**
 * Metadata requests (session init, status query, verification HEAD) carry no body, so a flat
 * deadline is generous. Body uploads are bounded by the progress watchdog (UPLOAD_STALL_MS) and,
 * once the body is sent, the response deadline (UPLOAD_RESPONSE_TIMEOUT_MS) instead.
 */
const GCS_METADATA_TIMEOUT_MS = 60_000

interface ContentRange {
  start: number
  end: number
  totalSize: number
}

interface UploadChunkParams {
  uri: SignedURLDTO
  chunk: Blob
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void
  abortSignal: AbortSignal
  contentRange: Nullable<ContentRange>
  /** Override Content-Type header (used when chunk.type is empty, e.g. RAW camera files) */
  contentType?: string
  /** Absolute deadline for the request; omitted where the stall watchdog and response deadline are the only bounds */
  timeoutMs?: number
}

// -- RESUMABLE UPLOAD HELPERS --

/** Parses GCS `Range: bytes=0-5000` header from status-check response */
const rangeRegex = /bytes=(\d+)-(\d+)/

function _parseRange(rangeHeader: string): ContentRange | null {
  const match = rangeHeader.match(rangeRegex)
  if (!match) return null

  const [, start, end] = match
  return {
    start: parseInt(start),
    end: parseInt(end),
    totalSize: 0, // Not provided in Range header; only start/end are used by caller
  }
}

/**
 * A transfer that moves no bytes for this long is stalled. Progress-based rather than size-based:
 * a size-derived deadline let a starved HTTP/2 stream hold an 8 MiB chunk for 224 s.
 */
const UPLOAD_STALL_MS = 30_000
// Fixed floor (~2 KB/s over 30 s counts as stalled); make it adaptive if legitimate links trip it.
const UPLOAD_STALL_MIN_BYTES = 64 * 1024

/**
 * How long a request may go unanswered once the browser reports its whole body sent. Firefox
 * reports that while the last ~1.5 KB of a chunk is still unsent, and GCS then waits ~6 min before
 * answering 408 (QA-460); a healthy chunk gets its response within seconds of the flush.
 * Only where no timeoutMs is set: a single PUT cannot resume, so its size-scaled timeoutMs bounds
 * the response instead of failing a slow but healthy link's buffered tail.
 */
const UPLOAD_RESPONSE_TIMEOUT_MS = 30_000

/**
 * Least a 308 must commit to count as progress rather than a failure. A front end that persists a
 * sliver per PUT would otherwise re-send the rest of the chunk indefinitely with no failure
 * recorded, holding a pool slot against every queued file.
 */
const MIN_308_ADVANCE_BYTES = 256 * 1024

/**
 * GCS cannot hold more bytes than the file has, and cannot un-persist bytes it already
 * acknowledged. An offset outside that window is garbled or forged, and trusting one past the end
 * empties the chunk queue — reporting success for bytes that were never sent.
 *
 * @param floor - bytes already known to be persisted
 */
function _isBelievablePersistedEnd(persistedEnd: number, floor: number, fileSize: number): boolean {
  return Number.isInteger(persistedEnd) && persistedEnd >= floor - 1 && persistedEnd <= fileSize - 1
}

export class UploadStalledError extends Error {
  constructor(public readonly bytesSent: number, public readonly phase: 'body' | 'response') {
    super(phase === 'response'
      ? `Upload stalled: no response ${UPLOAD_RESPONSE_TIMEOUT_MS} ms after the body was sent`
      : `Upload stalled: no progress for ${UPLOAD_STALL_MS} ms`)
    this.name = 'UploadStalledError'
  }
}

type ResumeStatus =
  | { kind: 'complete' }
  | { kind: 'partial', persistedEnd: number }
  | { kind: 'empty' }
  | { kind: 'unknown' }

/**
 * Asks GCS which bytes of a resumable session are persisted. Never throws: a cancelled or failed
 * query is reported as `unknown` and the caller checks the abort signal itself.
 *
 * @param knownPersisted - bytes already known to be persisted; an answer below it is not believed
 */
async function _getResumeStatus(uploadURI: string, file: File, abortSignal: AbortSignal, knownPersisted: number): Promise<ResumeStatus> {
  try {
    const response = await axios.put(uploadURI, null, {
      signal: abortSignal,
      timeout: GCS_METADATA_TIMEOUT_MS,
      headers: {
        'Content-Length': '0',
        'Content-Range': `bytes */${file.size}`,
      },
      // GCS returns 308 Resume Incomplete — axios treats non-2xx as error by default
      validateStatus: (status) => status === 308 || (status >= 200 && status < 300),
    })

    if (response.status !== 308) return { kind: 'complete' }

    const range = _parseRange(String(response.headers['range'] ?? ''))
    if (!range) return { kind: 'empty' }

    if (!_isBelievablePersistedEnd(range.end, knownPersisted, file.size)) {
      fileLogger.warn('Resumable upload status query returned an implausible Range', null, {
        context: {
          action: 'chunkUpload',
          metadata: {
            fileName: file.name,
            fileSize: file.size,
            persistedEnd: range.end,
            knownPersisted,
          },
        },
      })
      return { kind: 'unknown' }
    }

    return { kind: 'partial', persistedEnd: range.end }
  } catch (e) {
    if (!axios.isCancel(e)) {
      fileLogger.warn('Resumable upload status query failed', e, {
        context: {
          action: 'chunkUpload',
          // 404/410 means GCS expired the session — unrecoverable, unlike a transient 5xx
          metadata: {
            fileName: file.name,
            fileSize: file.size,
            status: axios.isAxiosError(e) ? e.response?.status : undefined,
          },
        },
      })
    }
    return { kind: 'unknown' }
  }
}

async function _uploadChunk(params: UploadChunkParams): Promise<AxiosResponse> {
  const { abortSignal, onUploadProgress, uri, chunk } = params
  const contentRangeHeader = params.contentRange ? {
    'Content-Range': `bytes ${params.contentRange.start}-${params.contentRange.end}/${params.contentRange.totalSize}`,
  } : {}

  // Own controller so a stall abort stays distinguishable from the caller's cancel
  const stall = new AbortController()
  const forwardAbort = () => stall.abort()
  if (abortSignal.aborted) stall.abort()
  abortSignal.addEventListener('abort', forwardAbort, { once: true })

  let lastLoaded = 0
  let loadedAtLastMark = 0
  let awaitingResponse = false
  let watchdog = setTimeout(() => stall.abort(), UPLOAD_STALL_MS)

  try {
    return await axios.put(
      uri.signedURL,
      chunk,
      {
        signal: stall.signal,
        timeout: params.timeoutMs,
        onUploadProgress: (progressEvent) => {
          lastLoaded = progressEvent.loaded ?? 0
          // Progress stops once the body is flushed, so swap the stall watchdog for the response
          // deadline. Armed once: a repeated full-progress event must not push it out.
          if (progressEvent.total !== undefined && lastLoaded >= progressEvent.total) {
            if (!awaitingResponse) {
              awaitingResponse = true
              clearTimeout(watchdog)
              if (params.timeoutMs === undefined) watchdog = setTimeout(() => stall.abort(), UPLOAD_RESPONSE_TIMEOUT_MS)
            }
          } else if (lastLoaded - loadedAtLastMark >= UPLOAD_STALL_MIN_BYTES) {
            loadedAtLastMark = lastLoaded
            clearTimeout(watchdog)
            watchdog = setTimeout(() => stall.abort(), UPLOAD_STALL_MS)
          }
          onUploadProgress(progressEvent)
        },
        // GCS returns 308 Resume Incomplete for intermediate chunks in resumable uploads
        validateStatus: (status) => (status >= 200 && status < 300) || status === 308,
        headers: {
          'Content-Type': params.contentType || chunk.type,
          ...contentRangeHeader,
          ...uri.headers,
        },
      }
    )
  } catch (e) {
    if (axios.isCancel(e) && !abortSignal.aborted) throw new UploadStalledError(lastLoaded, awaitingResponse ? 'response' : 'body')
    throw e
  } finally {
    clearTimeout(watchdog)
    abortSignal.removeEventListener('abort', forwardAbort)
  }
}

function _urlHost(url: string): string | undefined {
  try {
    return new URL(url).host
  } catch {
    return undefined
  }
}

/**
 * GCS derives the session host from the request, so a session URI off the signed host is not ours
 * and chunks must not be sent to it. Validates rather than rewrites: the WHATWG `protocol`/`host`
 * setters are no-ops on an opaque-path URL (`javascript:`, `data:`), so rewriting would hand back
 * the attacker's URI looking sanitised.
 *
 * @returns the session URI, or null when it is not https on the signed host
 */
function _sessionUriOnSignedHost(location: string, signedURL: string): string | null {
  const expectedHost = _urlHost(signedURL)
  if (!expectedHost) return null

  try {
    // Resolve against the signed URL so a scheme-less location cannot be passed through verbatim
    const session = new URL(location, signedURL)

    return session.protocol === 'https:' && session.host === expectedHost ? session.toString() : null
  } catch {
    return null
  }
}

/**
 * Result of file upload verification check.
 * Used to determine if an uploaded file was stored correctly on GCS.
 */
export interface VerificationResult {
  /** Whether the verification passed (file exists with size > 0) */
  success: boolean
  /** The stored content length from GCS, or null if unavailable */
  contentLength: number | null
  /** HTTP status of a failed HEAD, when the request got a response at all */
  status?: number
  /** Error message if verification failed */
  error?: string
}

/**
 * Verifies an uploaded file exists on GCS with exactly the bytes it was given.
 * Makes a HEAD request to check the `x-goog-stored-content-length` header.
 *
 * @param url - The verification URL (typically from SignedURLDTO.verifyURL)
 * @param expectedSize - Size of the local file, which the stored object must match
 * @returns Verification result with success status and content length
 *
 * @remarks
 * Only a stored size GCS actually reported is taken as confirmation, and only that is sent to
 * Sentry. A missing header or a failed request is returned as `success: false` without Sentry —
 * neither says anything about the bytes GCS holds.
 * Returns failure if:
 * - Content length is 0 (empty file uploaded)
 * - Content length differs from the file that was uploaded (truncated or foreign object)
 * - Header is missing (file may not exist)
 * - Network/request error occurs
 */
export const verifyUploadedFile = async (url: string, expectedSize: number): Promise<VerificationResult> => {
  try {
    const response = await axios.head(url, { timeout: GCS_METADATA_TIMEOUT_MS })
    const contentLengthHeader = response.headers['x-goog-stored-content-length']
    const contentLength = contentLengthHeader ? parseInt(contentLengthHeader, 10) : null

    if (contentLength === null || contentLength !== expectedSize) {
      const errorMessage = contentLength === null
        ? 'x-goog-stored-content-length header missing'
        : contentLength === 0
          ? 'File uploaded with 0 bytes'
          : `File stored with ${contentLength} bytes, expected ${expectedSize}`

      if (contentLength !== null) {
        captureException(new Error('FileAPI Upload verification failed'), {
          extra: { verifyUrl: url, contentLength, expectedSize, reason: errorMessage }
        })
      }

      return { success: false, contentLength, error: errorMessage }
    }

    return { success: true, contentLength }
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'Unknown verification error'

    return { success: false, contentLength: null, status: axios.isAxiosError(e) ? e.response?.status : undefined, error: errorMessage }
  }
}

/**
 * Completes an upload after a HEAD on the verify URL. A failed upload is only what GCS actually
 * confirmed: a stored size it reported that disagrees with the local file, or a 404. An expired
 * verify URL, CORS or a network error says nothing about the bytes GCS already accepted, and marking
 * those as failed made creatives re-upload healthy files. A 404 is not one of those — GCS answered,
 * and it says the object is not there.
 *
 * @param verifyUrl - URL to verify the upload, or undefined to skip verification
 * @param expectedSize - Size of the local file, which the stored object must match
 * @param onSuccess - Called when upload is verified successfully (or verification skipped)
 * @param onError - Called with error message when verification fails
 */
export const completeUploadWithVerification = async (
  verifyUrl: string | undefined,
  expectedSize: number,
  onSuccess: () => void,
  onError: (error?: string) => void
): Promise<void> => {
  if (!verifyUrl) {
    onSuccess()
    return
  }

  const result = await verifyUploadedFile(verifyUrl, expectedSize)

  if (result.success) {
    onSuccess()
    return
  }

  if (result.contentLength !== null || result.status === 404) {
    onError(result.error)
    return
  }

  fileLogger.warn('Upload verification inconclusive, treating the upload as successful', null, {
    context: { action: 'verifyUpload', metadata: { error: result.error, status: result.status, contentLength: result.contentLength } },
  })

  onSuccess()
}

/** Mints a fresh signed URL for one file. Rejecting or returning nothing falls through to the error path. */
export type SignedUrlRefresher = () => Promise<Nullable<SignedURLDTO>>

/**
 * Re-mints a signed URL that GCS rejected as expired or unsigned, returning null when the
 * caller supplied no refresher or the failure was something else.
 *
 * GCS answers an expired V4 signature with 401 ExpiredToken, and a signature it will not
 * accept with 403 SignatureDoesNotMatch. URLs are minted one pool-width wave at a time, so a
 * file holds its URL for at most one wave before initiating; this retry covers what is left of
 * that window, and only for callers that pass a refresher — the FileAPI path passes none. Note
 * 401s never reach Sentry on any path, hence the deliberate `null` payload on the log below.
 */
const _refreshRejectedSignedUrl = async (
  e: unknown,
  file: File,
  refreshSignedUrl: SignedUrlRefresher | undefined,
  action: string
): Promise<SignedURLDTO | null> => {
  if (!refreshSignedUrl || !axios.isAxiosError(e)) return null

  const status = e.response?.status
  if (status !== 401 && status !== 403) return null

  try {
    const refreshed = await refreshSignedUrl()
    if (!refreshed?.signedURL) {
      // The backend already reserved a placeholder for this re-mint; nothing downstream will ever see it
      void releaseLease(refreshed?.leaseId)
      return null
    }

    fileLogger.warn('Re-minted a signed URL GCS rejected as expired', null, {
      context: { action, metadata: { status, fileName: file.name, fileSize: file.size } },
    })

    return refreshed
  } catch (refreshError) {
    fileLogger.error('Re-minting a rejected signed URL failed', refreshError, {
      context: {
        action,
        metadata: { ...extractUploadErrorDetails(refreshError), status, fileName: file.name },
      },
    })
    return null
  }
}

/** Tracks the lease from the moment the file is queued until it settles, then releases it whatever the outcome */
const withLease = async (signedUrl: SignedURLDTO, upload: () => Promise<void>): Promise<void> => {
  trackLease(signedUrl.leaseId)
  try {
    await upload()
  } finally {
    void releaseLease(signedUrl.leaseId)
  }
}

const _uploadToGCResumable = async (
  file: File,
  signedUrl: SignedURLDTO,
  abortSignal: AbortSignal,
  onProgress: (percentCompleted: number) => void,
  onSuccess: () => void,
  onCancel: () => void,
  onError: (error?: string) => void,
  refreshSignedUrl?: SignedUrlRefresher,
  maxFailures = 10,
  onStart?: () => void
): Promise<void> => {
  onStart?.()

  // Cancelled while waiting for an upload slot
  if (abortSignal.aborted) {
    onCancel()
    return
  }

  if (rejectEmptyFile(file, 'resumableUploadInit', onError)) return

  // Fallback to application/octet-stream if browser doesn't recognize the file type (e.g. RAW camera files)
  const contentType = file.type || 'application/octet-stream'
  if (!file.type) {
    fileLogger.warn('File has no Content-Type, falling back to application/octet-stream', null, {
      context: {
        action: 'resumableUploadInit',
        metadata: { fileName: file.name, fileSize: file.size },
      },
    })
  }

  // Split file into fixed-size chunks for resilient uploads.
  // Without proactive chunking, large files (>500MB) are sent as a single HTTP request
  // that can timeout on slow connections. 8MB chunks keep each request short.
  const CHUNK_SIZE = 8 * 1024 * 1024 // 8 MB (GCS recommends multiples of 256 KB)
  const chunkRanges: [number, number][] = []
  for (let offset = 0; offset < file.size; offset += CHUNK_SIZE) {
    chunkRanges.push([offset, Math.min(offset + CHUNK_SIZE, file.size)])
  }

  // Initiate resumable upload
  const { headers, signedURL } = signedUrl

  let result: AxiosResponse<any, any>

  try {
    result = await axios.post(signedURL, null, {
      signal: abortSignal,
      timeout: GCS_METADATA_TIMEOUT_MS,
      headers: {
        ...headers,
        'Content-Type': contentType,
        'X-Upload-Content-Type': contentType,
      }
    })
  } catch (e) {
    // An abort landing between slot acquisition and this response must not create an
    // orphan resumable session or report as a failure.
    if (axios.isCancel(e)) {
      onCancel()
      return
    }

    const refreshed = await _refreshRejectedSignedUrl(e, file, refreshSignedUrl, 'resumableUploadInit')
    // No refresher passed on, so a re-minted URL gets exactly one attempt.
    // The re-mint reserved a new placeholder behind a new lease, so the retry carries its own.
    if (refreshed) return withLease(refreshed, () => _uploadToGCResumable(file, refreshed, abortSignal, onProgress, onSuccess, onCancel, onError, undefined, maxFailures))

    const errorDetails = extractUploadErrorDetails(e)
    fileLogger.error('Failed to initiate resumable upload', e, {
      context: {
        action: 'resumableUploadInit',
        metadata: {
          ...errorDetails,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        },
      },
    })
    onError(buildErrorMessage(e, 'Failed to initiate resumable upload'))
    return
  }

  const location = result.headers.location ? String(result.headers.location) : null
  const uploadURI = location && _sessionUriOnSignedHost(location, signedURL)

  if (!uploadURI) {
    fileLogger.error('Resumable upload init returned no usable session URI', null, {
      context: {
        action: 'resumableUploadInit',
        metadata: {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          location,
          expectedHost: _urlHost(signedURL),
        },
      },
    })
    onError('Failed to initiate resumable upload: no session URI')
    return
  }

  let uploadedBytes = 0
  let consecutiveFailures = 0
  let totalFailures = 0
  let responseDeadlineTrips = 0
  let finalized = false

  const rebuildFrom = (resumeStart: number) => {
    chunkRanges.length = 0
    uploadedBytes = resumeStart
    let offset = resumeStart
    while (offset < file.size) {
      // Stay on the original chunk grid: finish the partially persisted chunk, do not re-cut it
      const next = Math.min((Math.floor(offset / CHUNK_SIZE) + 1) * CHUNK_SIZE, file.size)
      chunkRanges.push([offset, next])
      offset = next
    }
  }

  // A resume can legitimately move uploadedBytes backwards; the bar reported to the user must not
  let reportedPercent = 0

  const handleChunkProgress = (progressEvent: AxiosProgressEvent) => {
    const totalProgress = Math.round(((uploadedBytes + (progressEvent.loaded ?? 0)) / file.size) * 100)
    reportedPercent = Math.min(Math.max(reportedPercent, totalProgress), 100)
    onProgress(reportedPercent)
  }

  // Counts a chunk that moved the upload nowhere and continues from whatever GCS actually holds.
  // Shared by a rejected chunk and by a 308 whose commit did not pass the chunk start, because
  // re-queueing the latter without counting it spins on a byte-identical request forever.
  // Returns false when the upload must stop.
  const recoverFromFailedChunk = async (e: unknown, message: string, start: number, end: number): Promise<boolean> => {
    const status = await _getResumeStatus(uploadURI, file, abortSignal, uploadedBytes)

    // A response-deadline trip that GCS shows moved past the 308 floor was a slow flush, not a failure:
    // count it like an accepted 308, so a link with a deep send buffer does not burn the budget chunk by chunk
    const responseTrip = e instanceof UploadStalledError && e.phase === 'response'
    const forgiven = responseTrip && status.kind === 'partial'
      && status.persistedEnd + 1 - start >= Math.min(MIN_308_ADVANCE_BYTES, end - start)
    if (responseTrip) responseDeadlineTrips++
    if (forgiven) {
      consecutiveFailures = 0
    } else {
      totalFailures++
      consecutiveFailures++
    }

    fileLogger[forgiven ? 'warn' : 'error'](forgiven ? 'Resumable upload response deadline passed after GCS took the chunk' : message, e, {
      context: {
        action: 'chunkUpload',
        metadata: {
          ...extractUploadErrorDetails(e),
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          chunkStart: start,
          chunkEnd: end,
          consecutiveFailures,
          totalFailures,
          stalled: e instanceof UploadStalledError,
          stalledAfterBytes: e instanceof UploadStalledError ? e.bytesSent : undefined,
          stalledPhase: e instanceof UploadStalledError ? e.phase : undefined,
        },
      },
    })

    if (abortSignal.aborted) {
      onCancel()
      return false
    }

    switch (status.kind) {
      case 'complete': rebuildFrom(file.size); break
      case 'partial': rebuildFrom(status.persistedEnd + 1); break
      case 'empty':
        // GCS never answers 308-without-Range once bytes are committed, so trusting that reply
        // after a commit would restart the whole file on every late failure
        if (uploadedBytes > 0) chunkRanges.unshift([start, end])
        else rebuildFrom(0)
        break
      case 'unknown': chunkRanges.unshift([start, end]); break
    }

    // warn, not info: info reaches neither Sentry nor a production console
    fileLogger.warn('Resumable upload resume decision', null, {
      context: {
        action: 'chunkUpload',
        metadata: {
          resumeKind: status.kind,
          resumeStart: uploadedBytes,
          chunkStart: start,
          fileSize: file.size,
        },
      },
    })

    return true
  }

  // Upload chunks sequentially. After any failure ask GCS what it persisted and continue from there;
  // the failure budget is per streak, so scattered blips on a long upload do not add up to an abort,
  // while the total budget stops a file that keeps being rebuilt without ever finishing.
  while (chunkRanges.length > 0 && consecutiveFailures < maxFailures && totalFailures < maxFailures * 3) {

    const [start, end] = chunkRanges.shift()!
    const chunk = file.slice(start, end, file.type)

    try {
      const response = await _uploadChunk({
        uri: { signedURL: uploadURI, headers: {}, filename: signedUrl.filename },
        contentRange: {
          start,
          end: end - 1,
          totalSize: file.size
        },
        chunk,
        abortSignal,
        contentType,
        onUploadProgress: handleChunkProgress,
      })

      // A 308 says how far GCS got in its Range header; a missing, implausible or barely-advanced
      // one is a failure, not progress. A chunk smaller than the floor need only commit all of itself.
      const resumeIncomplete = response.status === 308
      const range = resumeIncomplete ? _parseRange(String(response.headers['range'] ?? '')) : null
      const committedEnd = range && _isBelievablePersistedEnd(range.end, start, file.size) ? range.end : null
      const minAdvance = Math.min(MIN_308_ADVANCE_BYTES, end - start)

      if (resumeIncomplete && (committedEnd === null || committedEnd + 1 - start < minAdvance)) {
        const noProgress = new Error(committedEnd === null
          ? `Resumable upload chunk answered 308 with no usable Range: "${response.headers['range'] ?? ''}", chunk starts at ${start}`
          : `Resumable upload chunk advanced ${committedEnd + 1 - start} of the ${minAdvance} bytes required from ${start}`)
        if (!(await recoverFromFailedChunk(noProgress, 'Resumable upload chunk committed no new bytes', start, end))) return
        continue
      }

      consecutiveFailures = 0
      uploadedBytes = end
      // GCS answers the PUT that completes the object with a 2xx; a 308 means it is not finalized
      if (!resumeIncomplete && end === file.size) finalized = true
      if (committedEnd !== null && committedEnd + 1 < end) rebuildFrom(committedEnd + 1)
    } catch (e: any) {

      if (axios.isCancel(e)) {
        onCancel()
        return
      }

      if (!(await recoverFromFailedChunk(e, 'Resumable upload chunk failed', start, end))) return
    }

  }

  // Failed — exhausted retries before all chunks completed
  if (chunkRanges.length > 0) {
    fileLogger.error('Max upload failures exceeded', null, {
      context: {
        action: 'resumableUpload',
        metadata: {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          totalFailures,
          consecutiveFailures,
          maxFailures,
          remainingChunks: chunkRanges.length,
          uploadedBytes,
        },
      },
    })
    onError('Max upload failures exceeded')
    return
  }

  // The queue can also empty on a 308, which is GCS saying the object is NOT finalized. verifyURL is
  // optional, so without this the upload would report success on nothing but an emptied queue.
  if (!finalized) {
    const status = await _getResumeStatus(uploadURI, file, abortSignal, uploadedBytes)

    if (abortSignal.aborted) {
      onCancel()
      return
    }

    if (status.kind !== 'complete') {
      fileLogger.error('Resumable upload ended without a finalizing response', null, {
        context: {
          action: 'resumableUpload',
          metadata: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            resumeKind: status.kind,
            totalFailures,
            uploadedBytes,
          },
        },
      })
      onError('Upload did not finalize')
      return
    }
  }

  // Without this a file that stalled twice and recovered is indistinguishable from one that never did
  if (totalFailures > 0 || responseDeadlineTrips > 0) {
    fileLogger.warn('Resumable upload completed after recovery', null, {
      context: {
        action: 'resumableUpload',
        metadata: {
          totalFailures,
          responseDeadlineTrips,
          fileSize: file.size,
          fileName: file.name,
        },
      },
    })
  }

  await completeUploadWithVerification(signedUrl.verifyURL, file.size, onSuccess, onError)
}

/**
 * Axios call to signedUrl with resumable chunked upload and progress updates.
 * At most UPLOAD_CONCURRENCY files upload at once app-wide; excess calls queue (ISSUE-2560), and
 * `onStart` fires once the file holds a slot.
 * The file's lease is heartbeated from here until the upload settles.
 */
export const uploadToGCResumable = (...args: Parameters<typeof _uploadToGCResumable>) =>
  withLease(args[1], () => runInUploadPool(() => _uploadToGCResumable(...args)))

const _uploadToGC = async (
  file: File,
  signedUrl: SignedURLDTO,
  abortSignal: AbortSignal,
  onProgress: (percentCompleted: number) => void,
  onSuccess: () => void,
  onCancel: () => void,
  onError: (error?: string) => void,
  refreshSignedUrl?: SignedUrlRefresher,
  onStart?: () => void
): Promise<void> => {
  onStart?.()

  // Cancelled while waiting for an upload slot
  if (abortSignal.aborted) {
    onCancel()
    return
  }

  if (rejectEmptyFile(file, 'standardUpload', onError)) return

  // Fallback to application/octet-stream if browser doesn't recognize the file type (e.g. RAW camera files)
  if (!file.type) {
    fileLogger.warn('File has no Content-Type, falling back to application/octet-stream', null, {
      context: {
        action: 'standardUpload',
        metadata: { fileName: file.name, fileSize: file.size },
      },
    })
  }

  return _uploadChunk({
    uri: signedUrl,
    chunk: file,
    abortSignal,
    contentRange: null,
    contentType: file.type || 'application/octet-stream',
    // Whole-file upload has no resume path, so keep an absolute ceiling at a 50 kB/s floor
    timeoutMs: GCS_METADATA_TIMEOUT_MS + Math.ceil(file.size / (50 * 1024)) * 1000,
    onUploadProgress: (progressEvent) => {
      onProgress(Math.round((progressEvent.progress ?? 0) * 100))
    },
  })
    .then(async () => {
      await completeUploadWithVerification(signedUrl.verifyURL, file.size, onSuccess, onError)
    })
    .catch(async (e) => {
      if (axios.isCancel(e)) {
        onCancel()
      } else {
        const refreshed = await _refreshRejectedSignedUrl(e, file, refreshSignedUrl, 'standardUpload')
        // No refresher passed on, so a re-minted URL gets exactly one attempt.
        // The re-mint reserved a new placeholder behind a new lease, so the retry carries its own.
        if (refreshed) return withLease(refreshed, () => _uploadToGC(file, refreshed, abortSignal, onProgress, onSuccess, onCancel, onError))

        fileLogger.error('Standard upload to GCS failed', e, {
          context: {
            action: 'standardUpload',
            metadata: {
              ...extractUploadErrorDetails(e),
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
            },
          },
        })
        onError(buildErrorMessage(e, 'Upload failed'))
      }
    })
}

/**
 * Axios call to signedUrl with progress updates.
 * At most UPLOAD_CONCURRENCY files upload at once app-wide; excess calls queue (ISSUE-2560), and
 * `onStart` fires once the file holds a slot.
 * The file's lease is heartbeated from here until the upload settles.
 */
export const uploadToGC = (...args: Parameters<typeof _uploadToGC>) =>
  withLease(args[1], () => runInUploadPool(() => _uploadToGC(...args)))

export const uploadToGCWithoutLease = (...args: Parameters<typeof _uploadToGC>) =>
  runInUploadPool(() => _uploadToGC(...args))

// Initiates workers for visual loading
export const startLoadWorker = (() => {

  const workerCount = process.env.NODE_ENV === 'test'
    ? 0
    : navigator.hardwareConcurrency || 4

  const workerPool = [...Array(workerCount).keys()]
    .map(() => {
      const worker = new Worker(
        new URL('workers/loadFileWorker', import.meta.url),
        {
          type: 'module',
        }
      )
      return wrap<loadVisualWorkerType>(worker)
    })

  let i = 0

  /** Calls loadVisualWorker */
  return (loadUrl: string) => {
    const result = workerPool[i](loadUrl)
    i = ++i % workerCount
    return result
  }
})()
